import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import { PaginationMeta } from "../types/api";

const api = new APIClient();
const RESOURCE = "/marketing/contact-ruler";

export type ContactRulerStepKey =
  | "birthday"
  | "post_visit"
  | "protocol_care"
  | "return"
  | "reactivation";

export type ContactRulerChannel = "email" | "whatsapp" | "none";

export interface ContactRulerStep {
  key: ContactRulerStepKey;
  enabled: boolean;
  channel: ContactRulerChannel;
  quota_ok: boolean;
  config_path: string;
  summary: Record<string, unknown>;
}

export interface ContactRulerOverview {
  steps: ContactRulerStep[];
  plan_limits: {
    email_campaign_monthly_quota: number;
    campaign_used: number;
  };
  operational_chip: {
    key: string;
    label_key: string;
    config_path: string;
  };
}

export type ContactRulerPatch = {
  birthday_greetings_enabled?: boolean;
  birthday_lead_days?: number;
  appointment_reviews_enabled?: boolean;
  return_reminders_enabled?: boolean;
  return_reminder_lead_days?: number;
  reactivation_campaigns_enabled?: boolean;
  protocol_care_reminders_enabled?: boolean;
  contact_ruler_step_order?: ContactRulerStepKey[];
};

export interface ContactRulerPreview {
  subject: string;
  subject_b?: string;
  html: string;
  ab_hint?: string;
}

export interface LoyaltyNearRewardPreview {
  subject: string;
  html: string;
  whatsapp_body: string;
  channels: {
    email: { eligible: boolean; quota_ok: boolean };
    whatsapp: {
      eligible: boolean;
      messaging_outbound_enabled: boolean;
      quota_ok: boolean;
    };
  };
  sample: { remaining: number; label: string };
  config_path: string;
}

export interface ContactRulerReturnDueItem {
  client_id: number;
  client_name: string;
  service_id: number;
  service_name: string;
  unit_id?: number | null;
  due_on: string;
  last_completed_at: string;
  reference_appointment_id: number;
}

export const getContactRuler = async (): Promise<{
  success: boolean;
  data: ContactRulerOverview;
  message?: string;
}> => api.get(RESOURCE);

export const getBirthdayPreview = async (): Promise<{
  success: boolean;
  data: ContactRulerPreview;
  message?: string;
}> => api.get(`${RESOURCE}/birthday-preview`);

export const getReturnPreview = async (): Promise<{
  success: boolean;
  data: ContactRulerPreview;
  message?: string;
}> => api.get(`${RESOURCE}/return-preview`);

export const getProtocolCarePreview = async (): Promise<{
  success: boolean;
  data: ContactRulerPreview;
  message?: string;
}> => api.get(`${RESOURCE}/protocol-care-preview`);

export const getLoyaltyNearRewardPreview = async (): Promise<{
  success: boolean;
  data: LoyaltyNearRewardPreview;
  message?: string;
}> => api.get(`${RESOURCE}/loyalty-near-reward-preview`);

export const getReturnDue = async (params?: {
  page?: number;
  per_page?: number;
  days?: number;
}): Promise<{
  success: boolean;
  data: ContactRulerReturnDueItem[];
  meta: PaginationMeta;
  message?: string;
}> => api.get(`${RESOURCE}/return-due`, params);

export const downloadReturnDueCsv = async (days = 7): Promise<Blob> => {
  const blob = await axios.get<Blob>(`${RESOURCE}/return-due/export`, {
    params: { days },
    responseType: "blob",
  });

  return blob as unknown as Blob;
};

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const patchContactRuler = async (
  payload: ContactRulerPatch
): Promise<{
  success: boolean;
  data: ContactRulerOverview;
  message?: string;
}> => api.update(RESOURCE, payload);
